import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import {
    clearSessionContextOidc,
    fetchSessionContextOidcRequest,
} from '../common/reducers/sessionContextOidc';
import {
    OIDC_BASE,
    OIDC_TOKEN_URL,
    CLIENT_ID,
    CLIENT_SECRET,
    persistAuthArtifacts,
    finalizeAuth,
    exchangeCode,
    fetchUser,
    parseAuToken,
    clearAuthArtifacts as clearArtifacts,
    buildLogoutURL as logoutUrl,
} from '../utils/oidcTokenStorage';
import { isAccessTokenExpired } from '../utils/tokenRefresh';
import { getAuthToken } from '../utils/authManager';
import { isLocalhostWithDevToken, getLocalhostAu } from '../utils/envFromCookies';
import { isAdminHost } from '../router/hostContext';
import { PATHS } from '../router/paths';
import Cookies from 'js-cookie';
import { normalizePermissions } from '../common/normalizers/userPermission'

export const clearAuthArtifacts = clearArtifacts;
export const buildLogoutURL = logoutUrl;

export const Auth = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const handled = useRef(false);
    const [authState, setAuthState] = useState({ loading: true, authenticated: false });
    const [authError, setAuthError] = useState("");
    const [organizationChoices, setOrganizationChoices] = useState([]);
    const [tokenResponse, setTokenResponse] = useState(null);
    const [orgSelected, setOrgSelected] = useState(false);
    const [userSelected, setUserSelected] = useState(0);
    const [user, setUser] = useState(null);
    const startAuth = useCallback(() => {
        const authURL = new URL(`${process.env.exzeoid_base_url}/oidc/auth`);
        authURL.searchParams.append('client_id', process.env.exzeoid_client_id);
        authURL.searchParams.append('response_type', 'code');
        authURL.searchParams.append('redirect_uri', window.location.origin);
        authURL.searchParams.append('scope', 'openid email profile offline_access');
        authURL.searchParams.append('prompt', 'consent');
        authURL.searchParams.append('allowOfflineAccess', 'true');
        authURL.searchParams.append('useRefreshTokens', 'true');
        window.location.href = authURL.toString();
    }, []);
    
    useEffect(() => {
        async function fetchLoggedInUser(){
            let token = getAuthToken()
            const userResult = await fetchUser(token,Cookies.get('_co'))
            setUser(userResult)
        }
        if(authState.authenticated && user == null){
            fetchLoggedInUser()            
        }
        if(user != null){
            dispatch(clearSessionContextOidc());
            dispatch(fetchSessionContextOidcRequest());
            navigate(getLandingPage(userSelected, Cookies.get('_co'), user), {replace: true});
        }
    }, [authState.authenticated, user]);

    useEffect(() => {
        if (
        authState.loading
        || authState.authenticated
        || authError
        || organizationChoices.length
        ) {
        return;
        }

        startAuth();
    }, [authState.loading, authState.authenticated, authError, organizationChoices.length]);

    useEffect(() => {
        async function bootstrapAuth() {
            const code = new URLSearchParams(location.search).get('code');
            if (code && !handled.current) {
                try {
                    handled.current = true;
                    const result = await exchangeCode(code);
                    setAuthError("");            
                    if (result.selectedOrganization) {
                        setOrganizationChoices([]);
                        setTokenResponse(null);
                        setAuthState({ loading: false, authenticated: true });
                        setUserSelected(result.userId)
                    } else {
                        setOrganizationChoices(result.organizations);
                        setTokenResponse(result.tokenResponse);
                        setAuthState({ loading: false, authenticated: false });
                        //setUserSelected(result.userId)
                    }
                    return;
                } catch (exchangeError) {
                    // Do NOT auto-redirect to Auth0 here: the failure may
                    // be a persistent condition (CORS, gateway down, invalid
                    // config). Bouncing to Auth0 just yields a fresh ?code
                    // that will fail again, producing an infinite reload
                    // loop. Surface the error to the user and let them
                    // decide when to retry.
                    clearAuthArtifacts();
                    setOrganizationChoices([]);
                    setTokenResponse(null);
                    setAuthError(exchangeError.message || 'Authentication failed');
                    setAuthState({ loading: false, authenticated: false });
                }
            } else {
                // No ?code= in the URL means we're landing here fresh (root
                // path hit directly, a new tab, or authLoader bouncing an
                // unauthenticated admin-subdomain request back to "/"). If a
                // still-valid session already exists in cookies, reuse it
                // instead of restarting the external OIDC handshake — that
                // handshake always re-prompts organization selection for
                // multi-org users, even though the org was already picked.
                const existingToken = parseAuToken();
                const existingOrgId = Cookies.get('_co');
                if (
                    existingToken?.access_token
                    && existingOrgId
                    && !isAccessTokenExpired(existingToken.access_token)
                ) {
                    setOrganizationChoices([]);
                    setTokenResponse(null);
                    setAuthState({ loading: false, authenticated: true });
                    return;
                }
                setOrganizationChoices([]);
                setTokenResponse(null);
                setAuthState({ loading: false, authenticated: false });
                startAuth();
            }
        }
        bootstrapAuth();
    }, []);

    const getLandingPage = (userId, orgId, userObj) => {
        var access
        let mercuryOrgs = process.env.mercury_orgIds
        // userObj presence is the real "we identified this user" signal.
        // userId (only populated on the code-exchange/org-selection paths)
        // stays 0 when an existing session is reused from cookies alone
        // (e.g. a new tab), so gating on it too would wrongly send a valid
        // session to UNAUTH_LOGOUT.
        if(userObj != null){
            access = getAccessPermission(userObj?.permissions)
        }
        if(userObj && !userObj.user?.is_active){
            return PATHS.UNAUTH_LOGOUT
        }
        if (isAdminHost()) {
            return "/orgprofile"
        } else if(mercuryOrgs.split(",").map(Number).includes(orgId))
        {
            return "/mercury";
        }
        else if (!access)
        {
            console.log("no workkard or activity access")
            return PATHS.UNAUTH_LOGOUT;
        }
        else if (!access.hasWorkkardAccess && !access.hasActivityAccess)
        {
            return "/userprofile";
        }
        else if (access.hasWorkkardAccess)
        {
            return "/workkards";
        }
        else if (access.hasActivityAccess)
        {
            return "/activities";
        }
        
        return "/workkards"
    };

    const getAccessPermission = (userPermission) => {
        let isAdmin, hasActivityAccess, hasWorkkardAccess = false
        const normalizedPermissions = normalizePermissions(userPermission);
        if (
            normalizedPermissions.canAcceptRejectActivity
            || normalizedPermissions.canChangeActivityOwner
            || normalizedPermissions.canCloseActivity
            || normalizedPermissions.canCreateActivity
            || normalizedPermissions.canEditActivity
            || normalizedPermissions.canReassignActivity
            || normalizedPermissions.canReopenActivity
            || normalizedPermissions.canReceiveUnAssignedActivities
            || normalizedPermissions.canArchiveActivity
            || normalizedPermissions.canMarkActivityAsInProgress
            || normalizedPermissions.canCompleteActivity
            || normalizedPermissions.canCloseActivityduringMidterm
            || normalizedPermissions.canAcceptRejectBid
            || normalizedPermissions.canAssignB2B
        )
        {
            hasActivityAccess = true;
        }
        if (
            normalizedPermissions.canArchiveWorkkard
            || normalizedPermissions.canChangeWorkkardSponsor
            || normalizedPermissions.canCreateworkkard
            || normalizedPermissions.canEditWorkkard
            || normalizedPermissions.canReceiveUnAssignedWorkkards
        )
        {
            hasWorkkardAccess = true;
        }
        if (
            normalizedPermissions.canAddUser
            || normalizedPermissions.canEditUser
            || normalizedPermissions.canBlockUser
            || normalizedPermissions.canReAssignUser
            || normalizedPermissions.canResetPassword
            || normalizedPermissions.canManageSettings
            || normalizedPermissions.canManageCompanyProfile
        )
        {
            isAdmin = true;
        }
        return {isAdmin, hasWorkkardAccess, hasActivityAccess}
    }

    if (authError) {
        return (
            <div className="logoutContainer">
                <div className="exzeoLogo"><img src="/images/exzeo-logo.png" alt="Exzeo" /></div>
                <div className="logoutScreen" role="alert">
                    <div className="associateHead rowSpaceBetween alignMiddle">
                        <h3>Sign in failed</h3>
                    </div>
                    <p style={{ margin: '1rem 0' }}>{authError}</p>
                    <div className="accountAcces textCenter">
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setAuthError('');
                                startAuth();
                            }}
                        >
                            Try again
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    if (!authState.authenticated && organizationChoices.length) {
    return (
      <OrganizationSelectionScreen
        organizations={organizationChoices}
        busy={orgSelected}
        onSelect={(orgId) => {
          const selectedOrganization = organizationChoices.find(
            (organization) => `${organization.org_id}` === `${orgId}`,
          );
          if (!selectedOrganization) {
            setAuthError("Selected organization is not available");
            return;
          }

          setOrgSelected(true);
          try {
            finalizeAuth(tokenResponse, selectedOrganization);
            setUserSelected(selectedOrganization.user_id)
            setOrganizationChoices([]);
            setTokenResponse(null);
            setAuthError("");
            setAuthState({ loading: false, authenticated: true });
          } catch (selectionError) {
            clearAuthArtifacts();
            setAuthError(selectionError.message);
          } finally {
            setOrgSelected(false);
          }
        }}
      />
    );
  }
}

function OrganizationSelectionScreen({ organizations, busy, onSelect }) {
  return (
    <div className="logoutContainer">
      <div className="exzeoLogo"><img src="/images/exzeo-logo.png" alt="Exzeo" /></div>
      <div className="logoutScreen">
        <div className="associateHead rowSpaceBetween alignMiddle">
          <h3>You are associated with multiple organisations, select one to proceed</h3>
        </div>
        <ul className="organisations">
          {organizations.map((organization) => (
            <li onClick={() => onSelect(organization.org_id)} disabled={busy} className="owners" key={organization.org_id}>
              
                <span className="choice">
                  <img
                    src= {organization.profile_url || "/images/default_org.png"}
                    alt={organization.name}
                  />
                </span>
                <span className="tampaBox">
                  <h3>{organization.name}</h3>
                  <span>{formatOrganizationLocation(organization)}</span>
                </span>
              
            </li>
          ))}
        </ul>
        <div className="accountAcces textCenter"><a href={logoutUrl(window.location.origin)}>Back to login</a></div>
      </div>
      <div className="footer react-auth-footer">
        <div className="user">
          <a target="_blank" rel="noopener noreferrer" href={legalLink("/agreement")}>
            User Contract
          </a>
          <a target="_blank" rel="noopener noreferrer" href={legalLink("/privacypolicy")}>
            Privacy Policy
          </a>
        </div>
        <small>Copyright &copy; {new Date().getFullYear()} Exzeo USA, Inc. All rights reserved.</small>
      </div>
    </div>
  );
}

function formatOrganizationLocation(organization) {
  const city = `${organization?.city ?? ""}`.trim();
  const zip = `${organization?.zip ?? ""}`.trim();

  if (city && zip) {
    return `${city},${zip}`;
  }
  return city || zip;
}

function legalLink(path) {
  return new URL(path, window.location.origin).toString();
}